<script setup>
import Spinner from '@/components/Spinner.vue';
import FunPlayModal from './Partials/FunPlayModal.vue';
import Carousel from '@/components/App/Carousel.vue';
import JackpotCounter from '@/components/JackpotCounter.vue';
import { debounce } from '@/helpers';
import { useAuthStore } from '@/stores/auth';
import { useGamesStore } from '@/stores/games';
import { useResponseMessageStore } from '@/stores/response_message';
import { normalizeLang } from '@/helpers';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n';

const assetsUrl = import.meta.env.VITE_ASSETS_URL
const { t } = useI18n()
const router = useRouter()
const responseMessageStore = useResponseMessageStore()
const auth = useAuthStore()
const games = useGamesStore()
const { 
    getCategories, 
    getSubcategories, 
    getGames,
    getGame, 
    getNewGames, 
    getRecents,
    getProvidersWithoutLive
} = storeToRefs(games)
const { getCredits, getCurrency, getUser, isAuth, getJpTiers, getJpTiersLoading } = storeToRefs(auth)

const showSpinner = ref(false)
const opening = ref(false)
const search = ref('')
const searchResult = ref([])
const selectedGame = ref()
const modalRef = ref(null)
const lang = ref(normalizeLang(localStorage.getItem('lang')))
const sliderImages = [
  { src: '/egt.jpg', url: '/casino/provider/6797a05c68687d3ecd919e6f' },
  { src: '/pragmatic.jpg', url: '/casino/provider/67584aa2792b47680fe03982' },
  { src: '/novamatic.jpg', url: '/casino/provider/6759554aab3ab07722b78ca6' },
  { src: '/elk-studios.jpg', url: '/casino/provider/675954a1ab3ab07722b78c44' },
  { src: '/3-oaks.jpg', url: '/casino/provider/68a87a59cb4907c65bee6499' },
]

const activeJackpotIndex = ref(0)

const jackpotsList = computed(() => getJpTiers.value || [])

const activeJackpot = computed(() => {
  const list = jackpotsList.value
  const idx = activeJackpotIndex.value
  return list[idx] || null
})

const activeJackpotTiers = computed(() => {
  const j = activeJackpot.value
  const amounts = { grand: 0, major: 0, minor: 0, mini: 0 }
  const visible = { grand: false, major: false, minor: false, mini: false }
  const hot = { grand: false, major: false, minor: false, mini: false }
  const drop = { grand: false, major: false, minor: false, mini: false }
  if (!j || !Array.isArray(j.tiers)) return { amounts, visible, hot, drop }
  for (const tier of j.tiers) {
    const name = (tier.tier_name || '').toLowerCase()
    const isReady = !!tier.is_ready
    const isDrop = !!tier.drop
    if (name === 'grand') {
      amounts.grand = tier.contribution_amount ?? 0
      visible.grand = true
      hot.grand = isReady
      drop.grand = isDrop
    } else if (name === 'major') {
      amounts.major = tier.contribution_amount ?? 0
      visible.major = true
      hot.major = isReady
      drop.major = isDrop
    } else if (name === 'minor') {
      amounts.minor = tier.contribution_amount ?? 0
      visible.minor = true
      hot.minor = isReady
      drop.minor = isDrop
    } else if (name === 'mini') {
      amounts.mini = tier.contribution_amount ?? 0
      visible.mini = true
      hot.mini = isReady
      drop.mini = isDrop
    }
  }
  return { amounts, visible, hot, drop }
})

const tabLabel = (jackpot) => {
  const cur = getCurrency.value(jackpot.currency_id)
  return cur?.cod || jackpot.name || 'Jackpot'
}

const handlePlay = ({ demo }) => {
  launchGame(selectedGame.value, demo)
}

const openGame = (game) => {
    if(isAuth.value){
        if (game.has_demo === 'Y' && getUser.value?.c_p_demo) {
            selectedGame.value = game
            modalRef.value.openModal()
        } else {
            launchGame(game, false)
        }
    }else{
         router.push({ name: 'login' })
    }
}

const launchGame = async (game, isDemo) => {
  opening.value = true;

  try {
    const res = await games.openGame(game._id, {
      device: 'M',
      currency_id: getCredits.value[0].cuid._id,
      currency_name: getCurrency.value(getCredits.value[0].cuid._id).cod,
      homeURL: import.meta.env.VITE_APP_URL,
      demo: isDemo,
      lang: lang.value
    });

    opening.value = false;

    if (res.data?.nt === true) {
      window.open(res.data.data, '_blank');
      return;
    }

    if (res.data?.nt === false) {
      games.setGameUrl(res.data.data);
      router.push('/casino/game');
    }

  } catch (error) {
    opening.value = false;
    console.error(error);
  }
};

const startSearching = debounce(async () => {
    showSpinner.value = true
    await games.searchGames(search.value).then(res => {
        if (res.data.ok) {
            searchResult.value = res.data.data
            showSpinner.value = false
        }
    })
}, 500)


watch(search, (value) => {
    startSearching()
})

onMounted(async () => {
    games.refreshCasino()
    if(isAuth.value){
        games.fetchRecentsGames()
    }
})
</script>

<template>
    <div>
        <div class="SearchInput">
            <div class="SearchInput_Icon" />
            <input
                class="SearchInput_Field"
                :placeholder="$t('app.8')"
                type="search"
                v-model="search"
            />
        </div>
        <div v-show="!search">
            <div class="py-5 px-3">
                <Carousel
                    height="200px"
                    :items="sliderImages"
                    :slidesPerView="1"
                    :spaceBetween="20"
                    :showNavigation="false"
                    :showPagination="true"
                    :autoplay="false"
                    :swiperOptions="{ loop: true }"
                >
                    <template #slide="{ slide }">
                        <RouterLink :to="slide.url" class="block w-full h-full">
                            <img :src="slide.src" alt="" class="carousel-img rounded-lg cursor-pointer"/>
                        </RouterLink>
                    </template>
                </Carousel>
            </div>
            <div class="px-4">
                <ul class="list-none space-y-6">
                    <!-- all providers -->
                    <div>
                        <h3 class="text-lg font-bold text-white/75 cursor-pointer">{{ $t('games.providers') }}</h3>
                        <ul class="flex overflow-x-auto space-x-2 mt-3">
                            <li v-for="provider in getProvidersWithoutLive.sort((a, b) => a.ord - b.ord)" class="flex-1 px-2 bg-neutral-600/25 rounded-lg flex items-center justify-center">
                                <RouterLink :to="`/casino/providers`" class="block w-[94px] ">
                                    <img :src="provider.img" class="game-thumb h-[56px] w-full" :alt="provider.nm" loading="lazy">
                                </RouterLink>
                            </li>
                        </ul>
                    </div>
                    <div v-if="getJpTiersLoading || jackpotsList.length" class="jackpot-section">
                        <template v-if="getJpTiersLoading">
                            <div class="w-full flex flex-col items-center gap-3 py-8 px-2">
                                <div class="h-14 w-full max-w-[950px] rounded-lg bg-white/10 animate-pulse" />
                                <div class="h-12 w-[92%] max-w-[850px] rounded-lg bg-white/10 animate-pulse" />
                            </div>
                        </template>
                        <template v-else>
                            <div v-if="jackpotsList.length > 1" class="casino-jackpot-tabs">
                                <ul class="casino-jackpot-tabs__list">
                                    <li
                                        v-for="(jp, idx) in jackpotsList"
                                        :key="jp._id"
                                        role="button"
                                        tabindex="0"
                                        @click="activeJackpotIndex = idx"
                                        @keydown.enter.space.prevent="activeJackpotIndex = idx"
                                        :class="['casino-jackpot-tabs__pill', activeJackpotIndex === idx && 'casino-jackpot-tabs__pill--active']"
                                    >
                                        <span>{{ tabLabel(jp) }}</span>
                                    </li>
                                </ul>
                            </div>
                            <JackpotCounter
                                v-if="activeJackpot"
                                :grand-jackpot="activeJackpotTiers.amounts.grand"
                                :major-jackpot="activeJackpotTiers.amounts.major"
                                :minor-jackpot="activeJackpotTiers.amounts.minor"
                                :mini-jackpot="activeJackpotTiers.amounts.mini"
                                :visible-tiers="activeJackpotTiers.visible"
                                :grand-hot="activeJackpotTiers.hot.grand"
                                :major-hot="activeJackpotTiers.hot.major"
                                :minor-hot="activeJackpotTiers.hot.minor"
                                :mini-hot="activeJackpotTiers.hot.mini"
                                :grand-drop="activeJackpotTiers.drop.grand"
                                :major-drop="activeJackpotTiers.drop.major"
                                :minor-drop="activeJackpotTiers.drop.minor"
                                :mini-drop="activeJackpotTiers.drop.mini"
                                :auto-increment="false"
                            />
                        </template>
                    </div>
                    <!-- Recents Games -->
                    <li v-if="getRecents.length">
                        <h3 class="text-lg font-bold text-white/75 cursor-pointer">{{ $t('app.39') }}</h3>
                        <ul class="flex overflow-x-auto space-x-4 mt-3">
                            <li v-for="game in getRecents" class="flex-1">
                                <div class="w-[140px]" @click="openGame(game)">
                                    <img :src="game.img ? `${assetsUrl}/c/g/${game.img}` : game.extimg" class="game-thumb rounded-xl" alt="" loading="lazy">
                                </div>
                            </li>
                        </ul>
                    </li>
                    <!-- New Games -->
                    <div>
                        <h3 class="flex items-center gap-2 text-lg font-bold text-white/75 cursor-pointer">
                            <img src="/fire.svg"  class="w-4 h-4 "/>
                            {{ $t('app.40') }}
                        </h3>
                        <ul class="flex overflow-x-auto space-x-4 mt-3">
                            <li v-for="game in getNewGames" class="flex-1">
                                <div class="w-[140px]" @click="openGame(game)">
                                    <img :src="game.img ? `${assetsUrl}/c/g/${game.img}` : game.extimg" class="game-thumb rounded-xl" alt="" loading="lazy">
                                </div>
                            </li>
                        </ul>
                    </div>
                    <!-- top games -->
                    <div>
                        <h3 class="text-lg font-bold text-white/75 cursor-pointer mt-3" v-if="getGames('675839454f20898d9b11dde5', '67595674ab3ab07722b78d3e').length">
                            {{ $t('games.top') }}
                        </h3>
                        <ul class="flex overflow-x-auto space-x-4 mt-3">
                            <li v-for="game in getGames('675839454f20898d9b11dde5', '67595674ab3ab07722b78d3e')" class="flex-1">
                                <div class="w-[140px]" @click="openGame(game)">
                                    <img :src="game.img ? `${assetsUrl}/c/g/${game.img}` : game.extimg"  class="game-thumb rounded-xl" alt="" loading="lazy">
                                </div>
                            </li>
                        </ul>
                    </div>
                    <!-- jackpot games -->
                    <div>
                        <h3 class="text-lg font-bold text-white/75 cursor-pointer mt-3" v-if="getGames('675839454f20898d9b11dde5', '68d2aa746800dc300cf0d11d').length">
                            {{ $t('games.amusnet') }}
                        </h3>
                        <ul class="flex overflow-x-auto space-x-4 mt-3">
                            <li v-for="game in getGames('675839454f20898d9b11dde5', '68d2aa746800dc300cf0d11d')" class="flex-1">
                                <div class="w-[140px]" @click="openGame(game)">
                                    <img :src="game.img ? `${assetsUrl}/c/g/${game.img}` : game.extimg"  class="game-thumb rounded-xl" alt="" loading="lazy">
                                </div>
                            </li>
                        </ul>
                    </div>
                    
                    <li v-for="cat in getCategories.filter(c => c._id && c.nm !== 'Live Casino')">
                        <div>
                            <RouterLink :to="`casino/${cat._id}`" class="flex items-center gap-2 text-lg font-bold text-white/75 cursor-pointer mt-3">
                                <img src="/slots.svg" v-if="cat.nm == 'Slots'"  class="w-4 h-4"/>
                                <img src="/virtual.svg" v-else-if="cat.nm == 'Virtual'"  class="w-4 h-4"/>
                                 <img src="/fire.svg" v-else  class="w-6 h-6"/>
                                {{ $t('games.' + cat.nm.replace(' ', '_').toLowerCase()) }}
                            </RouterLink>

                            <ul v-for="sub in getSubcategories(cat._id).filter(s => s.main)" class="flex overflow-x-auto space-x-4 mt-3">
                                <li v-for="game in getGames(cat._id, sub._id)" class="flex-1">
                                    <div class="w-[140px]" @click="openGame(game)">
                                        <img :src="game.img ? `${assetsUrl}/c/g/${game.img}` : game.extimg"  class="game-thumb rounded-xl" alt="" loading="lazy">
                                    </div>
                                </li>
                            </ul>

                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <div v-show="search" class="px-3 grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4 mt-6 space-x-1">
            <template v-for="game in searchResult">
                <div class="cursor-pointer" @click="openGame(game)">
                    <img :src="game.img ? `${assetsUrl}/c/g/${game.img}` : game.extimg" class="game-thumb rounded-xl" alt="" loading="lazy">
                </div>
            </template>
        </div>

        <FunPlayModal
            ref="modalRef"
            :value="selectedGame"
            @play="handlePlay"
        />
        <Spinner :show="opening || showSpinner" />
    </div>
</template>

<style scoped>
.ActiveFilter {
    color:var(--color-primary4);
    .cat-icon{
        background-color:var(--color-primary4);
    }
}
.cat-icon{
    mask-position: center;
    mask-repeat: no-repeat;
    background-color: #BBBBBB; 
}
.icon-home {
  mask-image: url("/home.svg");

}
.icon-lc {
  mask-image: url("/livecasino.svg");

}
.icon-slots{
  mask-image: url("/slots.svg");
}
.icon-virtual{
  mask-image: url("/virtual.svg");
}
.icon-new{
  mask-image: url("/new.svg");
  background-color: #cd6505 ; 
}
.SearchInput {
    background-color: hsla(0,0%,100%,.1);
    border-radius: 50px;
    display: flex;
    @apply mx-3 mt-2 px-3;
}

.SearchInput_Icon {
    mask-image: url("/search.svg");
    mask-size: 18px 18px;
    mask-repeat: no-repeat;
    mask-position: center;
    min-width: 18px;
    background-color: #fff;
    z-index: 20;
    display: flex;
    flex-direction: column
}

.SearchInput_Field {
    @apply flex-1 text-sm !bg-transparent placeholder:text-white font-bold text-white py-2.5 border-0;
    @apply focus:ring-0;
}
.game-thumb {
 height: auto;
  width: 100%;
  min-height: 2px;    
  object-fit: cover;
}
.carousel-img {
  width: 100%;
  height: 200px; 
  object-fit: cover;
  border-radius: 0.5rem;
}

.casino-jackpot-tabs {
  @apply mx-3 mt-3 p-2 rounded-2xl;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--color-primary1) 35%, transparent),
    color-mix(in srgb, var(--color-primary1) 15%, transparent)
  );
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.12),
    0 8px 30px rgba(0,0,0,0.35);
}

.casino-jackpot-tabs__list {
  @apply flex items-center justify-center gap-2 list-none m-0 p-0;
}

.casino-jackpot-tabs__pill {
  @apply flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer text-white/80 text-sm font-semibold transition-all duration-200;
  background: color-mix(in srgb, var(--color-primary1) 12%, transparent);
}

.casino-jackpot-tabs__pill:hover {
  background: color-mix(in srgb, var(--color-primary1) 22%, transparent);
}

.casino-jackpot-tabs__pill--active {
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--color-primary1) 90%, white 5%),
    var(--color-primary1)
  );
  color: #fff;
  box-shadow:
    0 6px 18px rgba(0,0,0,0.45),
    inset 0 1px 0 rgba(255,255,255,0.25);
}

.casino-jackpot-tabs__pill--active span {
  font-weight: 700;
}
</style>
